/**
 * Настройки уведомлений: звук, вибрация и push хранятся в localStorage
 * под ключом `orion_notification_prefs`.
 *
 * Настройки нужны и экрану «Настройки», и обработчику веб-сокета, поэтому
 * они читаются из одного места. Веб-сокет спрашивает их лениво — при каждом
 * входящем сообщении, — чтобы переключатель срабатывал без переподключения.
 *
 * Хранилище вспомогательное: если оно недоступно (приватный режим браузера,
 * переполнение квоты), возвращаются значения по умолчанию, а чат работает
 * как раньше.
 */

/** Настройки уведомлений, которые пользователь может переключить сам. */
export interface NotificationPrefs {
    /** Звук входящего сообщения (по умолчанию включён). */
    sound: boolean;
    /** Вибрация на мобильном (по умолчанию включена). */
    vibration: boolean;
    /** Push-уведомления (по умолчанию выключены: разрешение спрашиваем явно). */
    push: boolean;
}

/** Ключ localStorage, под которым лежат настройки. */
export const NOTIFICATION_PREFS_STORAGE_KEY = 'orion_notification_prefs';

/** Значения по умолчанию: звук и вибрация включены, push — нет. */
export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
    sound: true,
    vibration: true,
    push: false,
};

/**
 * Прочитать настройки. Недостающие поля и битые значения заменяются
 * значениями по умолчанию — экран настроек не должен падать из-за
 * испорченной записи.
 */
export const readNotificationPrefs = (): NotificationPrefs => {
    let raw: string | null = null;
    try {
        raw = window.localStorage.getItem(NOTIFICATION_PREFS_STORAGE_KEY);
    } catch {
        return { ...DEFAULT_NOTIFICATION_PREFS };
    }

    if (!raw) return { ...DEFAULT_NOTIFICATION_PREFS };

    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') {
            return { ...DEFAULT_NOTIFICATION_PREFS };
        }
        const value = parsed as Partial<Record<keyof NotificationPrefs, unknown>>;

        return {
            sound: typeof value.sound === 'boolean' ? value.sound : DEFAULT_NOTIFICATION_PREFS.sound,
            vibration:
                typeof value.vibration === 'boolean'
                    ? value.vibration
                    : DEFAULT_NOTIFICATION_PREFS.vibration,
            push: typeof value.push === 'boolean' ? value.push : DEFAULT_NOTIFICATION_PREFS.push,
        };
    } catch {
        return { ...DEFAULT_NOTIFICATION_PREFS };
    }
};

/** Сохранить настройки целиком. */
export const writeNotificationPrefs = (prefs: NotificationPrefs): void => {
    try {
        window.localStorage.setItem(
            NOTIFICATION_PREFS_STORAGE_KEY,
            JSON.stringify({
                sound: prefs.sound,
                vibration: prefs.vibration,
                push: prefs.push,
            })
        );
    } catch {
        // Приватный режим или переполненная квота — настройки не критичны.
    }
};
